/*
  Adventskalender-Konfigurator

  Grundgedanke: Das Türchen-Brett ist ein Planungswerkzeug, kein Warenkorb.
  Der Kunde plant alle Türchen – auch die, die er mit eigenen Kleinigkeiten
  füllt – und bestellt bei uns nur die Artikel, die er bei uns holt. Die
  Türchen-Zuordnung bleibt deshalb bewusst beim Kunden und wandert NICHT als
  Zusatzinfo in den Warenkorb: Wir packen den Kalender nicht, die Nummer hätte
  für uns keinen Nutzen und würde gleiche Artikel in getrennte Zeilen splitten.
*/
(function () {
  'use strict';

  var STORAGE_KEY = 'makimo:advent:v1';

  function euro(cents) {
    return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  }

  // Base64 mit Umlauten: btoa kann nur Latin-1, deshalb der Umweg über UTF-8.
  function encodePlan(obj) {
    var b64 = btoa(unescape(encodeURIComponent(JSON.stringify(obj))));
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function decodePlan(str) {
    try {
      var b64 = str.replace(/-/g, '+').replace(/_/g, '/');
      while (b64.length % 4) b64 += '=';
      return JSON.parse(decodeURIComponent(escape(atob(b64))));
    } catch (e) {
      return null;
    }
  }

  class AdventConfigurator {
    constructor(root) {
      this.root = root;
      this.doors = parseInt(root.dataset.doors, 10) || 24;

      var dataEl = root.querySelector('[data-advent-products]');
      this.catalog = dataEl ? JSON.parse(dataEl.textContent) : [];
      this.catalog.forEach((p) => (p._source = 'collection'));

      this.el = {
        ageFilter: root.querySelector('[data-advent-age]'),
        priceFilter: root.querySelector('[data-advent-price]'),
        priceOut: root.querySelector('[data-advent-price-out]'),
        filters: root.querySelector('[data-advent-filters]'),
        grid: root.querySelector('[data-advent-grid]'),
        more: root.querySelector('[data-advent-more]'),
        empty: root.querySelector('[data-advent-empty]'),
        search: root.querySelector('[data-advent-search]'),
        searchClear: root.querySelector('[data-advent-search-clear]'),
        searchStatus: root.querySelector('[data-advent-search-status]'),
        board: root.querySelector('[data-advent-board]'),
        summary: root.querySelector('[data-advent-summary]'),
        countOut: root.querySelector('[data-advent-count]'),
        totalOut: root.querySelector('[data-advent-total]'),
        planList: root.querySelector('[data-advent-plan]'),
        reset: root.querySelector('[data-advent-reset]'),
        // Es gibt zwei: unter dem Brett und im Abschluss-Block. Beide teilen
        // sich Zustand und Aktion.
        addAll: Array.prototype.slice.call(root.querySelectorAll('[data-advent-add]')),
        share: root.querySelector('[data-advent-share]'),
        feedback: root.querySelector('[data-advent-feedback]'),
        dialog: root.querySelector('[data-advent-dialog]'),
        dialogBody: root.querySelector('[data-advent-dialog-body]'),
      };

      this.slots = new Array(this.doors).fill(null);
      this.openDoorIndex = null;
      this.lastFocus = null;
      this.searchTerm = '';
      this.searchHits = [];
      // Nicht alles auf einmal: 60 Kacheln erschlagen die Seite, gerade mobil.
      this.pageSize = 16;
      this.shown = this.pageSize;
      this.press = null;
      this.drag = null;
      this.dragFrom = -1;
      this.suppressClick = false;
      // Türchen, in die gerade ein Bild fliegt: bleiben bis zur Landung leer,
      // auch wenn das Brett zwischendurch neu gezeichnet wird.
      this.incoming = {};
      this.lastDoor = -1;

      this.restore().then(() => {
        this.bind();
        this.renderAll();
      });
    }

    /* ---------- Zustand laden und sichern ---------- */

    async restore() {
      var raw = new URLSearchParams(window.location.search).get('plan');
      var plan = raw ? decodePlan(raw) : null;

      if (!plan) {
        try {
          var stored = localStorage.getItem(STORAGE_KEY);
          if (stored) plan = JSON.parse(stored);
        } catch (e) {
          plan = null;
        }
      }
      if (!plan || !Array.isArray(plan.s)) return;

      // Der geteilte Link speichert nur Handles, damit die URL kurz bleibt.
      // Unbekannte Produkte werden einmalig nachgeladen.
      var unknown = plan.s
        .filter((e) => e && e[0] === 'p' && !this.findByHandle(e[1]))
        .map((e) => e[1]);
      await Promise.all([...new Set(unknown)].map((h) => this.fetchProduct(h)));

      plan.s.slice(0, this.doors).forEach((entry, i) => {
        if (!entry) return;
        if (entry[0] === 'o') {
          this.slots[i] = { kind: 'own', text: entry[1] || '' };
        } else if (entry[0] === 'p' && this.findByHandle(entry[1])) {
          this.slots[i] = { kind: 'product', handle: entry[1], variant: entry[2] };
        }
      });
    }

    save() {
      var plan = { s: this.slots.map(this.serializeSlot) };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(plan));
      } catch (e) {
        /* Privater Modus o. Ä. – der Plan lebt dann nur in dieser Sitzung. */
      }
      return plan;
    }

    serializeSlot(slot) {
      if (!slot) return null;
      if (slot.kind === 'own') return ['o', slot.text];
      return ['p', slot.handle, slot.variant];
    }

    /* ---------- Produktdaten ---------- */

    findByHandle(handle) {
      return this.catalog.find((p) => p.h === handle);
    }

    async fetchProduct(handle) {
      try {
        var res = await fetch('/products/' + handle + '.js');
        if (!res.ok) return null;
        var p = await res.json();
        var variants = p.variants.filter((v) => v.available).map((v) => ({ id: v.id, t: v.title }));
        if (!variants.length) return null;

        // Schon bekannt? Dann nur die Varianten nachtragen, statt einen
        // zweiten Eintrag anzulegen – sonst zeigt das Raster Doppelgänger.
        var existing = this.findByHandle(handle);
        if (existing) {
          existing.v = variants;
          existing.p = p.price;
          return existing;
        }

        var product = {
          h: p.handle,
          t: p.title,
          u: '/products/' + p.handle,
          i: p.featured_image ? p.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2') : '',
          p: p.price,
          a: '',
          v: variants,
          _source: 'search',
        };
        this.catalog.push(product);
        return product;
      } catch (e) {
        return null;
      }
    }

    /* ---------- Belegung ---------- */

    filled() {
      return this.slots.filter(Boolean).length;
    }

    productSlots() {
      return this.slots.filter((s) => s && s.kind === 'product');
    }

    countOf(handle) {
      return this.productSlots().filter((s) => s.handle === handle).length;
    }

    total() {
      return this.productSlots().reduce((sum, s) => {
        var p = this.findByHandle(s.handle);
        return sum + (p ? p.p : 0);
      }, 0);
    }

    add(handle, variantId) {
      var i = this.slots.indexOf(null);
      if (i === -1) {
        this.flash('Alle ' + this.doors + ' Türchen sind belegt. Leere eines, um zu tauschen.');
        return false;
      }
      var product = this.findByHandle(handle);
      if (!product) return false;
      var variant = variantId || (product.v[0] && product.v[0].id);
      if (!variant) return false;

      this.slots[i] = { kind: 'product', handle: handle, variant: variant };
      this.lastDoor = i;
      this.changed();
      return true;
    }

    /* ---------- Flug ins Türchen ---------- */

    captureFlight(trigger) {
      var tile = trigger.closest('.custom-advent__tile');
      var img = tile && tile.querySelector('.custom-advent__tile-media img');
      if (!img) return null;
      return { src: img.currentSrc || img.src, rect: img.getBoundingClientRect() };
    }

    flyToDoor(flight) {
      var index = this.lastDoor;
      var cell = this.doorCell(index);
      if (!cell) return;

      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!flight || reduce || typeof cell.animate !== 'function') {
        this.markLanded(index);
        return;
      }

      var from = flight.rect;
      var to = cell.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight;

      var size = Math.min(from.width, from.height);
      var fromX = from.left + from.width / 2;
      var fromY = from.top + from.height / 2;
      var toSize = Math.min(to.width, to.height);
      var toX = to.left + to.width / 2;
      var toY = to.top + to.height / 2;

      // Das Brett liegt über der Auswahl und ist beim Stöbern oft schon aus
      // dem Bild gescrollt. Dann fliegt das Bild in diese Richtung aus dem
      // Fenster, statt die Seite wegzuscrollen.
      var offscreen = false;
      if (to.bottom < 0) { toY = -toSize / 2; offscreen = true; }
      else if (to.top > viewport) { toY = viewport + toSize / 2; offscreen = true; }

      if (!offscreen) {
        this.incoming[index] = true;
        cell.classList.add('is-incoming');
      }

      var fly = document.createElement('img');
      fly.className = 'custom-advent__fly';
      fly.src = flight.src;
      fly.alt = '';
      fly.setAttribute('aria-hidden', 'true');
      fly.style.width = size + 'px';
      fly.style.height = size + 'px';
      fly.style.left = fromX - size / 2 + 'px';
      fly.style.top = fromY - size / 2 + 'px';
      document.body.appendChild(fly);

      var dx = toX - fromX;
      var dy = toY - fromY;
      var scale = toSize / size;

      var anim = fly.animate(
        [
          { transform: 'translate(0, 0) scale(1)', opacity: 1 },
          // Kurz anheben, als würde man den Artikel aufnehmen.
          { transform: 'translate(' + dx * 0.1 + 'px, -24px) scale(1.06)', opacity: 1, offset: 0.18 },
          { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(' + scale + ')', opacity: offscreen ? 0 : 1 },
        ],
        { duration: 700, easing: 'cubic-bezier(0.45, 0, 0.25, 1)', fill: 'forwards' }
      );

      var done = () => {
        fly.remove();
        if (offscreen) return;
        delete this.incoming[index];
        var current = this.doorCell(index);
        if (current) current.classList.remove('is-incoming');
        this.markLanded(index);
      };
      anim.onfinish = done;
      anim.oncancel = done;
    }

    doorCell(index) {
      if (!this.el.board || index < 0) return null;
      return this.el.board.querySelector('[data-door-cell="' + index + '"]');
    }

    markLanded(index) {
      var cell = this.doorCell(index);
      if (!cell) return;
      cell.classList.remove('is-landed');
      // Reflow erzwingen, damit die Animation auch bei schnellem
      // Nachklicken auf dasselbe Türchen neu startet.
      void cell.offsetWidth;
      cell.classList.add('is-landed');
      setTimeout(() => cell.classList.remove('is-landed'), 500);
    }

    removeOne(handle) {
      for (var i = this.slots.length - 1; i >= 0; i--) {
        var s = this.slots[i];
        if (s && s.kind === 'product' && s.handle === handle) {
          this.slots[i] = null;
          this.changed();
          return;
        }
      }
    }

    changed() {
      this.save();
      this.renderAll();
    }

    /* ---------- Filter ---------- */

    visibleProducts() {
      // Bei aktiver Suche zaehlt allein das Suchergebnis. Preis- und
      // Altersfilter greifen dort bewusst nicht: Wer gezielt sucht, will
      // genau das finden – auch wenn es teurer ist als der Regler zulaesst.
      if (this.searchTerm) return this.searchHits;

      var age = this.el.ageFilter ? this.el.ageFilter.value : '';
      var max = this.el.priceFilter ? parseInt(this.el.priceFilter.value, 10) * 100 : Infinity;

      return this.catalog.filter((p) => {
        if (p._source !== 'collection') return false;
        if (p.p > max) return false;
        if (age && !this.matchesAge(p, age)) return false;
        return true;
      });
    }

    matchesAge(product, age) {
      var value = product.a;
      if (!value) return false;
      if (Array.isArray(value)) return value.indexOf(age) !== -1;
      return String(value) === age;
    }

    /* ---------- Rendering ---------- */

    renderAll() {
      this.renderGrid();
      this.renderBoard();
      this.renderSummary();
      this.renderPlan();
    }

    renderGrid() {
      if (!this.el.grid) return;
      var products = this.visibleProducts();
      var visible = products.slice(0, this.shown);

      if (this.el.empty) this.el.empty.hidden = products.length > 0;
      this.el.grid.innerHTML = visible.map((p) => this.tileHTML(p)).join('');

      if (this.el.more) {
        var rest = products.length - visible.length;
        this.el.more.hidden = rest <= 0;
        this.el.more.textContent = 'Weitere ' + Math.min(rest, this.pageSize) + ' anzeigen';
      }
    }

    // Nur bei Filter- oder Suchwechsel zurueck auf Anfang. Beim Hinzufuegen
    // eines Artikels darf die Liste nicht wieder zusammenklappen.
    resetPaging() {
      this.shown = this.pageSize;
    }

    tileHTML(p) {
      var count = this.countOf(p.h);
      var variantSelect = '';
      if (p.v.length > 1) {
        variantSelect =
          '<select class="custom-advent__tile-variant" data-advent-variant="' + p.h + '" aria-label="Variante wählen">' +
          p.v.map((v) => '<option value="' + v.id + '">' + this.escape(v.t) + '</option>').join('') +
          '</select>';
      }

      return (
        '<li class="custom-advent__tile' + (count ? ' is-chosen' : '') + '">' +
        '<a class="custom-advent__tile-media" href="' + p.u + '" target="_blank" rel="noopener" title="Produktseite öffnen">' +
        (p.i ? '<img src="' + p.i + '" alt="" loading="lazy" width="200" height="200">' : '') +
        '</a>' +
        '<span class="custom-advent__tile-title">' + this.escape(p.t) + '</span>' +
        '<span class="custom-advent__tile-price">' + euro(p.p) + '</span>' +
        variantSelect +
        '<span class="custom-advent__stepper">' +
        '<button type="button" class="custom-advent__step-btn" data-advent-minus="' + p.h + '" aria-label="Ein Türchen weniger"' + (count ? '' : ' disabled') + '>−</button>' +
        '<span class="custom-advent__step-count" aria-live="polite">' + count + '</span>' +
        '<button type="button" class="custom-advent__step-btn" data-advent-plus="' + p.h + '" aria-label="In ein Türchen legen">+</button>' +
        '</span>' +
        '</li>'
      );
    }

    doorInner(slot) {
      if (slot && slot.kind === 'product') {
        var p = this.findByHandle(slot.handle);
        return (
          // draggable="false": Sonst startet der Browser beim Ziehen seinen
          // eigenen Bild-Zug, bricht die Pointer-Sequenz ab (pointercancel)
          // und das Verschieben kommt nie zustande.
          (p && p.i ? '<img class="custom-advent__door-img" src="' + p.i + '" alt="" draggable="false" loading="lazy">' : '') +
          '<span class="custom-advent__door-label">' + this.escape(p ? p.t : '') + '</span>'
        );
      }
      if (slot && slot.kind === 'own') {
        return '<span class="custom-advent__door-label">' + this.escape(slot.text || 'eigenes Teil') + '</span>';
      }
      return '';
    }

    renderBoard() {
      if (!this.el.board) return;

      this.el.board.innerHTML = this.slots
        .map((slot, i) => {
          var cls = 'custom-advent__door';
          if (slot && slot.kind === 'product') cls += ' is-filled';
          if (slot && slot.kind === 'own') cls += ' is-own';
          if (this.incoming[i]) cls += ' is-incoming';

          return (
            '<li class="' + cls + '" data-door-cell="' + i + '">' +
            '<button type="button" class="custom-advent__door-btn" data-advent-door="' + i + '" aria-label="Türchen ' + (i + 1) + ' bearbeiten">' +
            '<span class="custom-advent__door-num">' + (i + 1) + '</span>' +
            this.doorInner(slot) +
            '</button>' +
            '</li>'
          );
        })
        .join('');
    }

    renderSummary() {
      var products = this.productSlots().length;
      if (this.el.countOut) this.el.countOut.textContent = products;
      if (this.el.totalOut) this.el.totalOut.textContent = euro(this.total());
      this.el.addAll.forEach((btn) => (btn.disabled = products === 0));
      if (this.el.summary) this.el.summary.hidden = this.filled() === 0;
    }

    renderPlan() {
      if (!this.el.planList) return;
      this.el.planList.innerHTML = this.slots
        .map((slot, i) => {
          var what = '– noch offen –';
          if (slot && slot.kind === 'product') {
            var p = this.findByHandle(slot.handle);
            what = p ? p.t : 'Artikel';
          } else if (slot && slot.kind === 'own') {
            what = (slot.text || 'eigenes Teil') + ' (selbst besorgen)';
          }
          return (
            '<li class="custom-advent__plan-row">' +
            '<span class="custom-advent__plan-num">' + (i + 1) + '</span>' +
            '<span class="custom-advent__plan-what">' + this.escape(what) + '</span>' +
            '</li>'
          );
        })
        .join('');
    }

    escape(str) {
      var d = document.createElement('div');
      d.textContent = str == null ? '' : String(str);
      return d.innerHTML;
    }

    flash(message) {
      if (!this.el.feedback) return;
      this.el.feedback.textContent = message;
      this.el.feedback.hidden = false;
      clearTimeout(this._flashTimer);
      this._flashTimer = setTimeout(() => (this.el.feedback.hidden = true), 4000);
    }

    /* ---------- Türchen-Dialog ---------- */

    slotSummary(slot) {
      if (!slot) return 'noch offen';
      if (slot.kind === 'own') return slot.text || 'eigenes Teil';
      var p = this.findByHandle(slot.handle);
      return p ? p.t : 'Artikel';
    }

    openDialog(index) {
      var dialog = this.el.dialog;
      var body = this.el.dialogBody;
      if (!dialog || !body) return;

      var slot = this.slots[index];
      this.openDoorIndex = index;
      this.lastFocus = document.activeElement;

      var preview = '';
      if (slot && slot.kind === 'product') {
        var p = this.findByHandle(slot.handle);
        preview =
          '<div class="custom-advent__dialog-preview">' +
          (p && p.i ? '<img src="' + p.i + '" alt="">' : '') +
          '<span>' + this.escape(p ? p.t : 'Artikel') + '</span>' +
          '</div>';
      } else if (slot && slot.kind === 'own') {
        preview = '<div class="custom-advent__dialog-preview custom-advent__dialog-preview--own"><span>' + this.escape(slot.text || 'eigenes Teil') + '</span></div>';
      }

      // Verschieben-Auswahl: die Alternative zum Ziehen, die auf jedem Geraet
      // gleich zuverlaessig funktioniert.
      var moveOptions = this.slots
        .map((s, i) =>
          i === index
            ? ''
            : '<option value="' + i + '">Türchen ' + (i + 1) + (s ? ' – ' + this.escape(this.slotSummary(s)) : ' – frei') + '</option>'
        )
        .join('');

      // Das Textfeld nur anbieten, wenn es etwas einzutragen gibt: bei einem
      // leeren Türchen oder um eine eigene Beschriftung zu korrigieren. Liegt
      // dort schon ein Artikel von uns, wäre das Feld nur Rauschen – dann
      // führt der Weg über "Türchen leeren".
      var ownField = !slot || slot.kind === 'own';

      body.innerHTML =
        '<p class="custom-advent__dialog-title">Türchen ' + (index + 1) + '</p>' +
        preview +
        (ownField
          ? '<label class="custom-advent__dialog-field">' +
            '<span>Eigenes Teil eintragen</span>' +
            '<input type="text" data-advent-own-input value="' + (slot && slot.kind === 'own' ? this.escape(slot.text) : '') + '" placeholder="z. B. Kinokarte" maxlength="40">' +
            '</label>'
          : '') +
        (slot
          ? '<label class="custom-advent__dialog-field">' +
            '<span>An eine andere Stelle verschieben</span>' +
            '<select data-advent-move><option value="">Türchen wählen …</option>' + moveOptions + '</select>' +
            '</label>'
          : '') +
        '<div class="custom-advent__dialog-actions">' +
        (ownField ? '<button type="button" class="custom-advent__btn custom-advent__btn--primary" data-advent-own-save>Übernehmen</button>' : '') +
        (slot ? '<button type="button" class="custom-advent__btn" data-advent-door-clear>Türchen leeren</button>' : '') +
        '<button type="button" class="custom-advent__btn custom-advent__btn--quiet" data-advent-dialog-close>Abbrechen</button>' +
        '</div>';

      dialog.hidden = false;
      document.body.classList.add('custom-advent-dialog-open');

      var input = body.querySelector('[data-advent-own-input]');
      if (input) input.focus();
    }

    closeDialog() {
      if (!this.el.dialog) return;
      this.el.dialog.hidden = true;
      this.el.dialogBody.innerHTML = '';
      this.openDoorIndex = null;
      document.body.classList.remove('custom-advent-dialog-open');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }

    moveDoor(from, to) {
      var tmp = this.slots[to];
      this.slots[to] = this.slots[from];
      this.slots[from] = tmp;
      this.changed();
    }

    /* ---------- Türchen mit der Maus verschieben ----------
       Bewusst nur mit der Maus: Auf dem Handy fuellt das Brett die Breite,
       ein Ziehen wuerde dort mit dem Scrollen kollidieren. Touch-Nutzer
       verschieben ueber die Auswahl im Tuerchen-Dialog. */

    doorIndexAt(x, y) {
      var node = document.elementFromPoint(x, y);
      if (!node || !node.closest) return -1;
      var cell = node.closest('[data-door-cell]');
      if (!cell || !this.el.board.contains(cell)) return -1;
      return Number(cell.dataset.doorCell);
    }

    onDragStart(e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      var btn = e.target.closest('[data-advent-door]');
      if (!btn) return;
      var index = Number(btn.dataset.adventDoor);
      if (!this.slots[index]) return;   // leeres Tuerchen: nichts zu ziehen
      this.press = { index: index, x: e.clientX, y: e.clientY };
    }

    onDragMove(e) {
      if (!this.press) return;

      if (!this.drag) {
        if (Math.abs(e.clientX - this.press.x) < 6 && Math.abs(e.clientY - this.press.y) < 6) return;
        this.beginDrag(e.clientX, e.clientY);
        if (!this.drag) return;
      }

      this.drag.ghost.style.transform =
        'translate(' + (e.clientX - this.drag.dx) + 'px, ' + (e.clientY - this.drag.dy) + 'px)';

      var over = this.doorIndexAt(e.clientX, e.clientY);
      if (over !== this.drag.over) {
        var prev = this.el.board.querySelector('.is-drop-target');
        if (prev) prev.classList.remove('is-drop-target');
        if (over > -1 && over !== this.dragFrom) {
          var cell = this.el.board.querySelector('[data-door-cell="' + over + '"]');
          if (cell) cell.classList.add('is-drop-target');
        }
        this.drag.over = over;
      }
    }

    onDragEnd(e) {
      if (!this.drag) { this.press = null; return; }

      var to = this.doorIndexAt(e.clientX, e.clientY);
      var from = this.dragFrom;
      this.endDrag();
      this.press = null;
      // Nach dem Ziehen folgt noch ein click auf das Tuerchen - der wuerde
      // sonst den Dialog oeffnen.
      this.suppressClick = true;

      if (to > -1 && to !== from) this.moveDoor(from, to);
      else this.renderBoard();
    }

    beginDrag(x, y) {
      var from = this.press.index;
      var cell = this.el.board.querySelector('[data-door-cell="' + from + '"]');
      if (!cell) return;

      var rect = cell.getBoundingClientRect();
      var ghost = document.createElement('div');
      ghost.className = 'custom-advent__ghost';
      ghost.style.width = rect.width + 'px';
      ghost.style.height = rect.height + 'px';
      ghost.innerHTML = this.doorInner(this.slots[from]);
      document.body.appendChild(ghost);

      this.drag = { ghost: ghost, over: -1, dx: rect.width / 2, dy: rect.height / 2 };
      this.dragFrom = from;
      cell.classList.add('is-dragging');
      document.body.classList.add('custom-advent-dragging');
    }

    endDrag() {
      if (!this.drag) return;
      this.drag.ghost.remove();
      this.drag = null;
      document.body.classList.remove('custom-advent-dragging');
      this.el.board.querySelectorAll('.is-drop-target, .is-dragging').forEach(function (n) {
        n.classList.remove('is-drop-target');
        n.classList.remove('is-dragging');
      });
    }

    /* ---------- Suche nach beliebigen Produkten ---------- */

    async search(term) {
      // Unter zwei Zeichen nur das Suchergebnis zuruecknehmen, NICHT das
      // Feld leeren. Sonst loescht die verzoegerte Suche den ersten
      // Buchstaben, bevor der zweite getippt ist – mobil, wo man langsamer
      // als 250 ms pro Taste tippt, liess sich so gar nichts eingeben.
      if (term.length < 2) {
        this.resetSearch();
        return;
      }
      this.searchTerm = term;
      this.resetPaging();
      this.setSearchStatus('Suche läuft …');

      try {
        var url =
          '/search/suggest.json?q=' + encodeURIComponent(term) +
          '&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=hide';
        var res = await fetch(url);
        var data = await res.json();
        // Inzwischen weitergetippt? Dann gehoert diese Antwort zu einem alten
        // Begriff und darf das aktuelle Ergebnis nicht ueberschreiben.
        if (this.searchTerm !== term) return;
        var hits = (data.resources.results.products || []).filter((p) => p.available !== false);

        // Die Vorschlagssuche liefert keine Varianten-IDs. Wir zeigen die
        // Treffer trotzdem sofort an und tragen die IDs im Hintergrund nach –
        // so wartet niemand auf zehn Einzelabrufe, bevor etwas zu sehen ist.
        this.searchHits = hits.map((p) => {
          var known = this.findByHandle(p.handle);
          if (known) return known;
          return {
            h: p.handle,
            t: p.title,
            u: '/products/' + p.handle,
            i: p.image || '',
            p: Math.round(parseFloat(p.price) * 100),
            a: '',
            v: [],
            _source: 'search',
          };
        });

        this.renderGrid();
        this.setSearchStatus(
          hits.length
            ? hits.length + (hits.length === 1 ? ' Treffer' : ' Treffer') + ' für „' + term + '“'
            : 'Nichts gefunden für „' + term + '“'
        );

        var missing = this.searchHits.filter((p) => !p.v.length).map((p) => p.h);
        if (missing.length) {
          await Promise.all(missing.map((h) => this.fetchProduct(h)));
          if (this.searchTerm === term) this.renderGrid();
        }
      } catch (e) {
        this.setSearchStatus('Die Suche ist gerade nicht erreichbar.');
      }
    }

    // Suchergebnis zuruecknehmen, eingegebenen Text stehen lassen.
    resetSearch() {
      this.searchTerm = '';
      this.searchHits = [];
      this.resetPaging();
      this.setSearchStatus('');
      this.renderGrid();
    }

    // Ausdruecklich leeren: Escape, "Zuruecksetzen" oder das x im Feld.
    clearSearch() {
      if (this.el.search) this.el.search.value = '';
      this.resetSearch();
    }

    setSearchStatus(text) {
      var active = !!this.searchTerm;
      if (this.el.searchStatus) {
        this.el.searchStatus.textContent = text;
        this.el.searchStatus.hidden = !text;
      }
      if (this.el.searchClear) this.el.searchClear.hidden = !active;
      // Preis und Alter beziehen sich auf die Vorschlags-Collection – im
      // Suchergebnis waeren sie irrefuehrend. Nur abblenden, nicht
      // ausblenden: sonst springt die Suche darunter beim Tippen hoch.
      if (this.el.filters) {
        this.el.filters.classList.toggle('is-muted', active);
        this.el.filters.querySelectorAll('input, select, button').forEach((n) => {
          n.disabled = active;
        });
      }
    }

    /* ---------- Abschluss ---------- */

    async addAllToCart() {
      var counts = {};
      this.productSlots().forEach((s) => (counts[s.variant] = (counts[s.variant] || 0) + 1));
      var items = Object.keys(counts).map((id) => ({ id: Number(id), quantity: counts[id] }));
      if (!items.length) return;

      this.el.addAll.forEach((btn) => {
        btn.disabled = true;
        btn.classList.add('is-loading');
      });

      try {
        var res = await fetch((window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items }),
        });
        var data = await res.json();
        if (!res.ok) {
          this.flash(data.description || 'Das hat nicht geklappt. Bitte noch einmal versuchen.');
          return;
        }

        document.dispatchEvent(new CustomEvent('cart:refresh'));

        var drawer = document.getElementById('Cart-Drawer');
        if (drawer) {
          document.body.classList.add('open-cc');
          drawer.classList.add('active');
          document.dispatchEvent(new CustomEvent('cart-drawer:open'));
        } else {
          window.location.href = '/cart';
        }
      } catch (e) {
        this.flash('Das hat nicht geklappt. Bitte noch einmal versuchen.');
      } finally {
        this.el.addAll.forEach((btn) => {
          btn.disabled = false;
          btn.classList.remove('is-loading');
        });
      }
    }

    shareLink() {
      var plan = this.save();
      var url = window.location.origin + window.location.pathname + '?plan=' + encodePlan(plan);

      if (navigator.share) {
        navigator.share({ title: 'Mein Adventskalender-Plan', url: url }).catch(() => {});
        return;
      }
      navigator.clipboard
        .writeText(url)
        .then(() => this.flash('Link kopiert – jetzt kannst du deinen Plan verschicken.'))
        .catch(() => window.prompt('Link zum Kopieren:', url));
    }

    /* ---------- Ereignisse ---------- */

    bind() {
      var el = this.el;

      if (el.ageFilter) {
        el.ageFilter.addEventListener('change', () => {
          this.resetPaging();
          this.renderGrid();
        });
      }
      if (el.more) {
        el.more.addEventListener('click', () => {
          this.shown += this.pageSize;
          this.renderGrid();
        });
      }
      if (el.priceFilter) {
        var syncPrice = () => {
          if (el.priceOut) el.priceOut.textContent = euro(parseInt(el.priceFilter.value, 10) * 100);
          this.resetPaging();
          this.renderGrid();
        };
        el.priceFilter.addEventListener('input', syncPrice);
        syncPrice();
      }

      this.root.addEventListener('click', (e) => {
        var plus = e.target.closest('[data-advent-plus]');
        if (plus) {
          var handle = plus.dataset.adventPlus;
          var sel = this.root.querySelector('[data-advent-variant="' + handle + '"]');
          var product = this.findByHandle(handle);
          // Aus der Suche kann die Varianten-ID noch fehlen, wenn jemand
          // schneller tippt als das Nachladen läuft.
          // Startpunkt VOR dem Hinzufügen messen: add() zeichnet das Raster
          // neu, danach gibt es die Kachel in dieser Form nicht mehr.
          var flight = this.captureFlight(plus);
          if (product && !product.v.length) {
            plus.disabled = true;
            this.fetchProduct(handle).then((p) => {
              plus.disabled = false;
              if (p && p.v.length) {
                if (this.add(handle, p.v[0].id)) this.flyToDoor(flight);
              } else {
                this.flash('Dieser Artikel lässt sich gerade nicht hinzufügen.');
              }
            });
            return;
          }
          if (this.add(handle, sel ? Number(sel.value) : null)) this.flyToDoor(flight);
          return;
        }
        var minus = e.target.closest('[data-advent-minus]');
        if (minus) {
          this.removeOne(minus.dataset.adventMinus);
          return;
        }
        var door = e.target.closest('[data-advent-door]');
        if (door) {
          if (this.suppressClick) { this.suppressClick = false; return; }
          this.openDialog(Number(door.dataset.adventDoor));
          return;
        }
        if (e.target.closest('[data-advent-search-clear]')) {
          this.clearSearch();
          if (el.search) el.search.focus();
          return;
        }

        // Klick auf die abgedunkelte Flaeche schliesst den Dialog.
        if (e.target === el.dialog || e.target.closest('[data-advent-dialog-close]')) {
          this.closeDialog();
          return;
        }
        if (e.target.closest('[data-advent-door-clear]')) {
          this.slots[this.openDoorIndex] = null;
          this.closeDialog();
          this.changed();
          return;
        }
        if (e.target.closest('[data-advent-own-save]')) {
          var input = el.dialogBody.querySelector('[data-advent-own-input]');
          var text = input ? input.value.trim() : '';
          var i = this.openDoorIndex;
          if (text) this.slots[i] = { kind: 'own', text: text };
          this.closeDialog();
          this.changed();
        }
      });

      if (el.board) {
        // Zweite Absicherung gegen den nativen Bild-/Textzug des Browsers.
        el.board.addEventListener('dragstart', (e) => e.preventDefault());
        el.board.addEventListener('pointerdown', (e) => this.onDragStart(e));
        document.addEventListener('pointermove', (e) => this.onDragMove(e));
        document.addEventListener('pointerup', (e) => this.onDragEnd(e));
        document.addEventListener('pointercancel', () => {
          this.endDrag();
          this.press = null;
        });
      }

      if (el.dialogBody) {
        el.dialogBody.addEventListener('change', (e) => {
          var move = e.target.closest('[data-advent-move]');
          if (!move || move.value === '') return;
          var from = this.openDoorIndex;
          var to = Number(move.value);
          this.closeDialog();
          this.moveDoor(from, to);
        });
      }

      if (el.search) {
        var timer;
        el.search.addEventListener('input', () => {
          clearTimeout(timer);
          timer = setTimeout(() => this.search(el.search.value.trim()), 250);
        });
        el.search.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            this.clearSearch();
          }
        });
        // Das native x in Suchfeldern loest kein input-Ereignis in allen
        // Browsern aus – deshalb zusaetzlich auf search hoeren.
        el.search.addEventListener('search', () => {
          if (!el.search.value.trim()) this.clearSearch();
        });
      }

      if (el.reset) {
        el.reset.addEventListener('click', () => {
          if (!window.confirm('Wirklich alle Türchen leeren?')) return;
          this.slots = new Array(this.doors).fill(null);
          this.changed();
        });
      }
      el.addAll.forEach((btn) => btn.addEventListener('click', () => this.addAllToCart()));
      if (el.share) el.share.addEventListener('click', () => this.shareLink());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && el.dialog && !el.dialog.hidden) this.closeDialog();
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-advent-configurator]').forEach((root) => {
      if (root.dataset.adventReady) return;
      root.dataset.adventReady = 'true';
      new AdventConfigurator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
